/**
 * [PURPOSE]: Pure utilities for shop generation. The full
 *   generateShop() flow lives in App.jsx and is tightly coupled to
 *   React state (setRecentShopRolls, setGeneratedShop, etc.). This
 *   module hosts the shape-agnostic dispatcher so it can be unit-tested
 *   and reused across the d100, simple-mode, and advanced-mode paths.
 *
 * [WHY]: P1.2+P1.3 fix (Apr 2026). Pre-fix, the generator was split:
 *   - generateShopWithD100 understood the DMG `rolls` array shape.
 *   - The Simple Mode branch in generateShop understood
 *     `{common, uncommon, rare}` arrays.
 *   When a user picked a DMG table (rolls-array shape) but the calling
 *   path was Simple Mode, the shape mismatch caused a fallthrough.
 *
 *   Per Decision 2 in the fix prompt: keep `rolls` as the canonical
 *   format, dispatch on shape so both coexist. This module is that
 *   dispatcher.
 *
 * [TEMPLATES — APR 30 2026]: 23 of 290 MAGIC_ITEM_TABLES entries are
 *   pseudo-IDs that reference a CATEGORY of items rather than a specific
 *   BASE_ITEMS row ("Weapon, +1", "Armor, +3", "Belt of Hill Giant
 *   Strength", "Ammunition, +2"). These entries now carry a `template`
 *   field; rollFromTable dispatches to the resolver in templateResolvers.js
 *   and returns a `resolved` object alongside the raw entry. Callers that
 *   recognize the resolved shape use it directly (and skip getItemDefinition);
 *   callers that don't fall through to the flat-ID lookup as before.
 *
 * [INTEGRATION]: App.jsx generateShop() and generateShopWithD100()
 *   continue to own the React-state side of generation; they call
 *   rollFromTable() to get a single entry per d100 roll.
 */

import { resolveTemplateEntry, TEMPLATE_RESOLVERS } from './templateResolvers';
// [PHASE B / #033 — 2026-05-06]: Roll-time entry migration into
//   `{kind, ref, qty}` so the loot-grant pipeline (#034) and any
//   `kind`-aware consumer always sees the structured shape. Idempotent
//   — already-structured entries pass through by reference.
import { migrateEntry } from '../utils/rollTableEntrySchema';

/**
 * [PHASE B / #051 — 2026-05-07]: Resolve a roll-table id against the
 *   three known sources, in priority order. Pre-fix, App.jsx
 *   generateShopWithD100 only consulted DMG_LOOT_TABLES + SHOP_ROLL_TABLES
 *   on lines 6221/6229/6237/6244-6247 — custom roll-table ids
 *   (`custom_table_<ts>`) were silently dropped, so an Advanced shop
 *   built around a player-authored table generated zero items and
 *   produced the user-visible "No items generated! Roll table items
 *   may not exist in BASE_ITEMS." failure.
 *
 *   Per Rule 22, this helper is the single dispatcher that BOTH App.jsx
 *   AND the unit test import. Lookup is a no-op when sources are missing,
 *   so callers don't need to gate on availability.
 *
 * @param {string} tableId
 * @param {object} sources - { dmgTables?, shopTables?, customTables? }
 *   - dmgTables: object map keyed by tableId (DMG_LOOT_TABLES /
 *     MAGIC_ITEM_TABLES)
 *   - shopTables: object map keyed by tableId (SHOP_ROLL_TABLES)
 *   - customTables: array of {id, ...} (App.jsx customRollTables state)
 * @returns {object|null} the matching table object or null
 */
export function findRollTableById(tableId, sources = {}) {
  if (!tableId || typeof tableId !== 'string') return null;
  const { dmgTables, shopTables, customTables } = sources;
  if (dmgTables && dmgTables[tableId]) return dmgTables[tableId];
  if (shopTables && shopTables[tableId]) return shopTables[tableId];
  if (Array.isArray(customTables)) {
    const match = customTables.find((t) => t && t.id === tableId);
    if (match) return match;
  }
  return null;
}

/**
 * Roll a single entry from a roll table, dispatching on shape.
 *
 * @param {object} table - One of:
 *   - { rolls: [{min, max, item, name, rarity}, ...] }   (DMG d100)
 *   - { common: [...], uncommon: [...], rare: [...] }     (legacy)
 * @returns {object|null} - On hit, returns the entry with optional
 *   `resolved: { id, baseItem, properties, ... }` attached when the
 *   entry referenced a template. Returns null on miss/empty.
 */
export function rollFromTable(table) {
  if (!table || typeof table !== 'object') return null;

  // [Shape 1]: DMG d100 rolls array. Canonical format used by
  // MAGIC_ITEM_TABLES and the imported DMG Tables A-I.
  if (Array.isArray(table.rolls)) {
    if (table.rolls.length === 0) return null;
    const roll = Math.floor(Math.random() * 100) + 1;
    const entry = table.rolls.find((r) => roll >= r.min && roll <= r.max);
    if (!entry) return null;
    // [TEMPLATES]: If the entry references a registered template,
    // resolve it and attach the result so callers can skip the BASE_ITEMS
    // lookup. Falls through cleanly when entry.template is missing or
    // unrecognized — callers continue to use entry.item with
    // getItemDefinition() for flat-ID entries.
    if (entry.template && TEMPLATE_RESOLVERS[entry.template]) {
      const resolved = resolveTemplateEntry(entry);
      if (resolved) {
        // [PHASE B / #033]: Stamp `kind`/`ref`/`qty` so the loot-grant
        //   pipeline (#034) and any downstream `kind`-aware consumer
        //   sees the structured shape on every roll. Legacy fields
        //   (item, template, n, name, rarity) are preserved.
        return migrateEntry({ ...entry, resolved });
      }
    }
    // [PHASE B / #033]: Same migration for non-template flat-ID
    //   entries. Roll-time call is cheap (idempotent — already-
    //   structured entries pass by reference).
    return migrateEntry(entry);
  }

  // [Shape 2]: Legacy rarity-pool arrays. Used by SHOP_ROLL_TABLES
  // entries authored before the d100 migration (general, weapon_shop,
  // armor_shop, etc.). Returns an entry shape compatible with the
  // rolls-array consumers so the generator doesn't have to branch.
  if (
    Array.isArray(table.common) ||
    Array.isArray(table.uncommon) ||
    Array.isArray(table.rare)
  ) {
    const pools = [];
    if (Array.isArray(table.common) && table.common.length > 0) {
      pools.push({ rarity: 'common', items: table.common });
    }
    if (Array.isArray(table.uncommon) && table.uncommon.length > 0) {
      pools.push({ rarity: 'uncommon', items: table.uncommon });
    }
    if (Array.isArray(table.rare) && table.rare.length > 0) {
      pools.push({ rarity: 'rare', items: table.rare });
    }
    if (pools.length === 0) return null;

    // Equal weighting per rarity present. The shop-size config in the
    // caller decides HOW MANY to draw at each rarity; this helper just
    // produces ONE entry on demand.
    const pool = pools[Math.floor(Math.random() * pools.length)];
    const item = pool.items[Math.floor(Math.random() * pool.items.length)];
    return { item, rarity: pool.rarity };
  }

  return null;
}
